/** Curated interface themes that style the complete application. */
const VAR_KEYS = [
  '--color-primary', '--color-primary-dark', '--color-primary-soft', '--color-primary-border',
  '--bg-page', '--bg-card', '--bg-muted', '--text-primary', '--text-secondary', '--text-muted',
  '--border-color', '--border-subtle', '--header-bg', '--header-text', '--header-border',
  '--brand-gradient', '--focus-ring', '--radius', '--radius-lg', '--shadow-sm', '--shadow',
  '--shadow-md', '--shadow-lg', '--shadow-xl', '--font-family',
];

export const THEME_DEFINITIONS = [
  {
    id: 'professional', label: 'Professional', description: 'Clean and neutral', mode: 'light',
    preview: ['#172033', '#ffffff', '#2563eb'],
    vars: {
      '--color-primary': '37 99 235', '--color-primary-dark': '29 78 216',
      '--color-primary-soft': '#eff6ff', '--color-primary-border': '#bfdbfe',
      '--bg-page': '#f5f7fa', '--bg-card': '#ffffff', '--bg-muted': '#f8fafc',
      '--text-primary': '#172033', '--text-secondary': '#475569', '--text-muted': '#64748b',
      '--border-color': '#dfe4ea', '--border-subtle': '#edf0f4', '--header-bg': '#172033',
      '--header-text': '#ffffff', '--header-border': 'rgba(255,255,255,0.12)',
      '--brand-gradient': 'linear-gradient(135deg, #172033 0%, #24324a 100%)',
      '--focus-ring': 'rgb(37 99 235 / 0.32)', '--radius': '0.375rem', '--radius-lg': '0.5rem',
      '--shadow-sm': '0 1px 2px rgb(15 23 42 / 0.04)', '--shadow': '0 1px 3px rgb(15 23 42 / 0.07)',
      '--shadow-md': '0 4px 10px rgb(15 23 42 / 0.08)', '--shadow-lg': '0 12px 24px rgb(15 23 42 / 0.10)',
      '--shadow-xl': '0 20px 35px rgb(15 23 42 / 0.12)',
      '--font-family': '"Segoe UI", Inter, ui-sans-serif, system-ui, sans-serif',
    },
  },
  {
    id: 'executive', label: 'Executive', description: 'Navy and structured', mode: 'light',
    preview: ['#0f2942', '#ffffff', '#087e8b'],
    vars: {
      '--color-primary': '8 126 139', '--color-primary-dark': '15 97 106',
      '--color-primary-soft': '#ecfeff', '--color-primary-border': '#a5f3fc',
      '--bg-page': '#f3f6f8', '--bg-card': '#ffffff', '--bg-muted': '#f5f8fa',
      '--text-primary': '#12263a', '--text-secondary': '#41566b', '--text-muted': '#6b7f91',
      '--border-color': '#d8e0e6', '--border-subtle': '#eaf0f3', '--header-bg': '#0f2942',
      '--header-text': '#ffffff', '--header-border': 'rgba(255,255,255,0.13)',
      '--brand-gradient': 'linear-gradient(135deg, #0f2942 0%, #164e63 100%)',
      '--focus-ring': 'rgb(8 126 139 / 0.30)', '--radius': '0.25rem', '--radius-lg': '0.375rem',
      '--shadow-sm': '0 1px 2px rgb(15 41 66 / 0.04)', '--shadow': '0 1px 4px rgb(15 41 66 / 0.08)',
      '--shadow-md': '0 5px 12px rgb(15 41 66 / 0.09)', '--shadow-lg': '0 14px 28px rgb(15 41 66 / 0.11)',
      '--shadow-xl': '0 22px 40px rgb(15 41 66 / 0.13)',
      '--font-family': 'Inter, "Segoe UI", ui-sans-serif, system-ui, sans-serif',
    },
  },
  {
    id: 'clarity', label: 'Clarity', description: 'Bright and accessible', mode: 'light',
    preview: ['#ffffff', '#f1f5f9', '#4338ca'],
    vars: {
      '--color-primary': '67 56 202', '--color-primary-dark': '55 48 163',
      '--color-primary-soft': '#eef2ff', '--color-primary-border': '#c7d2fe',
      '--bg-page': '#ffffff', '--bg-card': '#ffffff', '--bg-muted': '#f8fafc',
      '--text-primary': '#0f172a', '--text-secondary': '#334155', '--text-muted': '#64748b',
      '--border-color': '#cbd5e1', '--border-subtle': '#e2e8f0', '--header-bg': '#ffffff',
      '--header-text': '#0f172a', '--header-border': '#cbd5e1',
      '--brand-gradient': 'linear-gradient(135deg, #312e81 0%, #4338ca 100%)',
      '--focus-ring': 'rgb(67 56 202 / 0.34)', '--radius': '0.5rem', '--radius-lg': '0.625rem',
      '--shadow-sm': '0 1px 2px rgb(15 23 42 / 0.05)', '--shadow': '0 1px 3px rgb(15 23 42 / 0.08)',
      '--shadow-md': '0 4px 8px rgb(15 23 42 / 0.09)', '--shadow-lg': '0 10px 22px rgb(15 23 42 / 0.11)',
      '--shadow-xl': '0 18px 34px rgb(15 23 42 / 0.13)',
      '--font-family': '"Open Sans", "Segoe UI", ui-sans-serif, system-ui, sans-serif',
    },
  },
  {
    id: 'midnight', label: 'Midnight', description: 'Focused dark workspace', mode: 'dark',
    preview: ['#111827', '#1f2937', '#38bdf8'],
    vars: {
      '--color-primary': '56 189 248', '--color-primary-dark': '14 165 233',
      '--color-primary-soft': 'rgba(56,189,248,0.12)', '--color-primary-border': 'rgba(56,189,248,0.28)',
      '--bg-page': '#111827', '--bg-card': '#1f2937', '--bg-muted': '#182231',
      '--text-primary': '#f8fafc', '--text-secondary': '#cbd5e1', '--text-muted': '#94a3b8',
      '--border-color': '#374151', '--border-subtle': '#2a3545', '--header-bg': '#0b1220',
      '--header-text': '#f8fafc', '--header-border': 'rgba(255,255,255,0.10)',
      '--brand-gradient': 'linear-gradient(135deg, #0b1220 0%, #16243a 100%)',
      '--focus-ring': 'rgb(56 189 248 / 0.38)', '--radius': '0.375rem', '--radius-lg': '0.5rem',
      '--shadow-sm': '0 1px 2px rgb(0 0 0 / 0.22)', '--shadow': '0 2px 5px rgb(0 0 0 / 0.24)',
      '--shadow-md': '0 6px 14px rgb(0 0 0 / 0.26)', '--shadow-lg': '0 14px 30px rgb(0 0 0 / 0.30)',
      '--shadow-xl': '0 22px 45px rgb(0 0 0 / 0.34)',
      '--font-family': 'Inter, "Segoe UI", ui-sans-serif, system-ui, sans-serif',
    },
  },
];

export const THEME_OPTIONS = THEME_DEFINITIONS.map(({ id, label, description, mode, preview }) => ({
  id, label, description, mode, preview,
}));

export function getThemeDefinition(themeId) {
  return THEME_DEFINITIONS.find((theme) => theme.id === themeId) || THEME_DEFINITIONS[0];
}

export function getThemeVars(themeId) {
  return getThemeDefinition(themeId).vars;
}

export { VAR_KEYS };
